import { Fragment, useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import { Link } from "react-router";

import { renderCell } from "@/components/table/DataTable";
import { ConflictNotice } from "@/components/table/ConflictBanner";
import { InlineEditCell } from "@/components/table/InlineEditCell";
import type { DataTableColumn } from "@/components/table/columns";
import {
  useInlineEdits,
  type EditConflictResolution,
  type InlineEditCommitArgs,
  type InlineEditConflictArgs,
} from "@/components/table/use-inline-edits";
import type { RecordField } from "@/components/record/fields";
import { RelatedRecordsSection, type RelatedRecordsSectionProps } from "@/components/record/RelatedRecords";
import { FieldRow } from "@/components/record/FieldRow";
import { Button } from "@/components/ui/Button";
import { useIsReadOnly } from "@/lib/current-actor";
import { labelFor } from "@/lib/field-labels";
import { cellKey, nextEditableCell, previousEditableCell, type EditCommitOutcome } from "@/lib/inline-edit";
import { showToast } from "@/lib/toast-queue";
import { useLoadingPhase } from "@/lib/use-loading-phase";

/**
 * EXPLORATORY SPIKE (2026-08-27): the header's own tint band + icon badge,
 * reusing `Home.tsx`'s already-shipped `vibrant-*` tokens (see `index.css`'s
 * own spike comment on those) rather than inventing a second decorative
 * palette. Not yet reconciled with DESIGN.md.
 */
const HEADER_TINT_CLASSES: Record<"indigo" | "cyan" | "violet" | "amber", string> = {
  indigo: "bg-vibrant-indigo-quiet",
  cyan: "bg-vibrant-cyan-quiet",
  violet: "bg-vibrant-violet-quiet",
  amber: "bg-vibrant-amber-quiet",
};

const HEADER_ICON_TINT_CLASSES: Record<"indigo" | "cyan" | "violet" | "amber", string> = {
  indigo: "bg-surface text-vibrant-indigo",
  cyan: "bg-surface text-vibrant-cyan",
  violet: "bg-surface text-vibrant-violet",
  amber: "bg-surface text-vibrant-amber",
};

/**
 * The one record-page shell every record-type screen reuses (mirroring
 * `DataTable`'s own "one component, every list" — R-W2's sibling promise
 * for record pages). Configured by `fields` and nothing else — no
 * conditional branch anywhere in this file keys on a route, an entity
 * name, or a specific record's identity
 * (`frontend/tests/architecture/record-page-generic.test.ts` enforces this
 * the same way `data-table-generic.test.ts` already does for the table).
 *
 * Field rows reuse Story 2.3's exact inline-edit mechanism at row
 * granularity: this component owns one `useInlineEdits` instance over a
 * single-row, multi-field "table" — the identical hook, the identical
 * `InlineEditCell`, the identical conflict shape (`ConflictNotice`, sharing
 * `ConflictBanner`'s own implementation) `DataTable` already uses for cells
 * across many rows. The caller (a route) owns fetching and the actual
 * mutating request, exactly as it does for `DataTable`.
 */
export interface RecordPageProps<Row extends { id: string }> {
  fields: RecordField<Row>[];
  row: Row | undefined;
  status: "loading" | "not-found" | "error" | "ready";
  errorMessage?: string;
  identifier: (row: Row) => string;
  statusBadge?: (row: Row) => ReactNode;
  /** A link back to this record's own list (Owner-caught, 2026-08-27: the
   * only way back from any record page was the nav rail — mirrors
   * `AlertInstanceHistory.tsx`'s own pre-existing "&larr; Back to alerts"
   * pattern, generalized here so every record type gets it, not just
   * alerts). Absent renders nothing, so a record page reachable with no
   * obvious single list (there are none today) is not forced to fake one. */
  backLink?: { label: string; href: string };
  /** The header's own entity icon + tint band (Owner-requested, 2026-08-27:
   * "bolder visual treatment" — see `Home.tsx`'s identical `vibrant-*`
   * tint system). `icon` absent still renders the tint band with no badge;
   * `tint` defaults to "indigo" the same way `StatTile.tsx` does. */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  tint?: "indigo" | "cyan" | "violet" | "amber";
  /**
   * Story 15.8's own horizontal "key facts" strip — a generic slot, exactly
   * like `relatedRecords` below: this shell renders whatever `ReactNode`
   * the caller supplies directly beneath the header (above every field
   * row), and knows nothing about what a "key fact" is. Absent renders
   * nothing, so every record page that predates this story is unaffected.
   */
  keyFacts?: (row: Row) => ReactNode;
  /** R-U4's 30-day restore affordance (spec judgement calls 3, 4): present
   * only for a soft-deleted record still inside the window. This shell
   * knows nothing about "restoring" beyond rendering the sentence and,
   * when `primaryAction` is supplied alongside it, the one action beside
   * it — the caller decides what that action actually does. */
  deletion?: { deletedAt: string; deletedByDisplayName: string } | undefined;
  /** The header's one primary action (DESIGN.md: "exactly one primary
   * action per screen", VC-2.5-06). Absent renders none. */
  primaryAction?: { label: string; onClick: () => void; disabled?: boolean } | undefined;
  /**
   * Ghost header actions (Story 2.6) — ready-made for exactly one shape:
   * a verb-labelled trigger that opens the caller's own `ConfirmDialog`,
   * never the destructive variant itself (DESIGN.md: "a destructive
   * action in a table row or record header is a ghost button labelled
   * with its verb"). This is the record page's own reachable equivalent
   * of a table row's hover-revealed ghost action — DESIGN.md § Component
   * Patterns "Data table row": "on phone they live on the record page,
   * never behind a swipe" — a phone visitor has no hover-revealed row
   * action to reach, so the record page is where that action actually
   * lives for them, not an omission.
   */
  secondaryActions?: { label: string; onClick: () => void; disabled?: boolean; ariaLabel?: string }[];
  onCommitEdit?: (args: InlineEditCommitArgs<Row>) => Promise<EditCommitOutcome>;
  onResolveConflict?: (args: InlineEditConflictArgs<Row>) => Promise<EditCommitOutcome>;
  relatedRecords?: RelatedRecordsSectionProps[];
}

const DATE_PROSE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
  year: "numeric",
  timeZone: "America/New_York",
});

function formatDateProse(isoDateTime: string): string {
  return DATE_PROSE_FORMATTER.format(new Date(isoDateTime));
}

export function RecordPage<Row extends { id: string }>({
  fields,
  row,
  status,
  errorMessage,
  identifier,
  statusBadge,
  backLink,
  icon: Icon,
  tint = "indigo",
  keyFacts,
  deletion,
  primaryAction,
  secondaryActions,
  onCommitEdit,
  onResolveConflict,
  relatedRecords,
}: RecordPageProps<Row>) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [activeField, setActiveField] = useState<string | null>(null);
  const isReadOnly = useIsReadOnly();
  const loadingPhase = useLoadingPhase(status === "loading");

  const primaryFields = fields.filter((field) => field.primary !== false);
  const moreFields = fields.filter((field) => field.primary === false);
  const visibleFields = moreOpen ? fields : primaryFields;

  // A soft-deleted record's fields are read-only, whatever `onCommitEdit`
  // was supplied (Edge-case matrix: "no inline editing of a deleted
  // record") — and a Read-only account sees no edit affordance anywhere,
  // the same withholding mechanism `DataTable` already uses.
  const editingAllowed = deletion === undefined && !isReadOnly;
  const editableColumns: DataTableColumn<Row>[] = editingAllowed ? visibleFields : [];
  const rows = row === undefined ? [] : [row];

  const inlineEdits = useInlineEdits<Row>({
    rows,
    columns: editableColumns,
    onCommit: onCommitEdit ?? (() => Promise.reject(new Error("no onCommitEdit configured"))),
    ...(onResolveConflict ? { onResolveConflict } : {}),
  });

  function rawFieldValue(field: DataTableColumn<Row>): string {
    if (row === undefined) {
      return "";
    }
    const value = row[field.key];
    return value === null || value === undefined ? "" : String(value);
  }

  function activateField(key: string) {
    if (!editingAllowed) {
      return;
    }
    setActiveField(key);
  }

  function commitActiveField(field: DataTableColumn<Row>, value: string) {
    setActiveField(null);
    if (row === undefined || value === rawFieldValue(field)) {
      return;
    }
    inlineEdits.commit(row, field, value);
  }

  /**
   * Story 15.5 — auto-save on navigate-away. `InlineEditCell` calls this
   * from its own unmount cleanup, in place of `commitActiveField` above,
   * when the field being torn down was active with a genuinely uncommitted
   * `draft` — the route just changed out from under an open, never-blurred
   * field. This uses the exact same write (`inlineEdits.commit`'s awaitable
   * twin, not a second commit mechanism), so the request on the wire is
   * identical to what blur would have sent.
   *
   * The one difference: by the time this resolves, the field itself is
   * already gone — `setActiveField`/`RecordPage`'s own field-level
   * resting/error rendering has nothing left to update (this may even be a
   * *different* record page entirely, post-navigation). `DESIGN.md`'s
   * "errors render next to the thing that failed" cannot apply when the
   * thing that failed no longer exists on screen — a toast is the honest,
   * practical exception here (mirrors the one other place this codebase
   * already reaches for `showToast(error.message)` on a page-level action
   * with nowhere inline to render into, `LeaseRecord.tsx`'s
   * `handleStartRenewal`). Success and failure are worded distinctly on
   * purpose (VC-15.5-04/05) so a quick read can never mistake one for the
   * other.
   */
  function commitFieldOnLeave(field: DataTableColumn<Row>, value: string) {
    if (row === undefined || value === rawFieldValue(field)) {
      return;
    }
    void inlineEdits.commitAwaitable(row, field, value).then((outcome) => {
      if (outcome.ok) {
        showToast("Saved.");
      } else {
        showToast("Didn't save. Please make that change again.");
      }
    });
  }

  function tabFromActiveField(field: DataTableColumn<Row>, value: string) {
    setActiveField(null);
    if (row === undefined) {
      return;
    }
    if (value !== rawFieldValue(field)) {
      inlineEdits.commit(row, field, value);
    }
    const next = nextEditableCell(rows, editableColumns, { rowId: row.id, columnKey: field.key });
    if (next !== null) {
      setActiveField(next.columnKey);
    }
  }

  function shiftTabFromActiveField(field: DataTableColumn<Row>, value: string) {
    setActiveField(null);
    if (row === undefined) {
      return;
    }
    if (value !== rawFieldValue(field)) {
      inlineEdits.commit(row, field, value);
    }
    const previous = previousEditableCell(rows, editableColumns, { rowId: row.id, columnKey: field.key });
    if (previous !== null) {
      setActiveField(previous.columnKey);
    }
  }

  function resolveConflict(field: DataTableColumn<Row>, resolution: EditConflictResolution) {
    if (row === undefined) {
      return;
    }
    void inlineEdits.resolveConflict(row, field, resolution);
  }

  function renderField(field: RecordField<Row>, layout: "row" | "card" = "row"): ReactNode {
    if (row === undefined) {
      return null;
    }
    const resting = renderCell(field, row);
    const provenance = field.provenance ? field.provenance(row) : undefined;
    const editable = field.edit !== undefined && editingAllowed;
    const key = cellKey(row.id, field.key);
    const cellState = inlineEdits.cellStates[key];

    const fieldRow = !editable ? (
      <FieldRow label={labelFor(field.key)} provenance={provenance} warningSentence={field.warningSentence} layout={layout}>
        {resting}
      </FieldRow>
    ) : (
      <FieldRow
        label={labelFor(field.key)}
        provenance={provenance}
        warningSentence={field.warningSentence}
        onActivate={() => activateField(field.key)}
        layout={layout}
      >
        <InlineEditCell
          row={row}
          column={field}
          rawValue={rawFieldValue(field)}
          isActive={activeField === field.key}
          onActivate={() => activateField(field.key)}
          cellState={cellState}
          onCommit={(value) => commitActiveField(field, value)}
          onCommitOnLeave={(value) => commitFieldOnLeave(field, value)}
          onCancel={() => setActiveField(null)}
          onTab={(value) => tabFromActiveField(field, value)}
          onShiftTab={(value) => shiftTabFromActiveField(field, value)}
          onRetry={() => inlineEdits.retry(row, field)}
          hasNextEditableCell={
            nextEditableCell(rows, editableColumns, { rowId: row.id, columnKey: field.key }) !== null
          }
          hasPreviousEditableCell={
            previousEditableCell(rows, editableColumns, { rowId: row.id, columnKey: field.key }) !== null
          }
        >
          {resting}
        </InlineEditCell>
      </FieldRow>
    );

    const conflict =
      cellState?.status === "conflict" ? (
        <ConflictNotice
          message={cellState.message ?? "Someone else changed this while it was being edited."}
          onKeepMine={() => resolveConflict(field, "mine")}
          onKeepTheirs={() => resolveConflict(field, "theirs")}
        />
      ) : null;

    // A grid's own children are its direct grid items — a `Fragment`'s two
    // children would become two separate cells instead of one card, so a
    // real wrapper div is what makes this field (plus its own conflict
    // banner, when present) one grid item.
    if (layout === "card") {
      return (
        <div key={field.key}>
          {fieldRow}
          {conflict}
        </div>
      );
    }

    return (
      <Fragment key={field.key}>
        {fieldRow}
        {conflict}
      </Fragment>
    );
  }

  if (status === "error") {
    return (
      <div className="rounded-lg border border-border-hairline bg-surface p-6 text-body text-ink">
        <p>Couldn&apos;t load this record.</p>
        {errorMessage ? <p className="mt-1 text-ink-secondary">{errorMessage}</p> : null}
      </div>
    );
  }

  if (status === "not-found") {
    return (
      <div className="rounded-lg border border-border-hairline bg-surface p-6 text-body text-ink">
        <p>That record could not be found. It may have already been removed.</p>
      </div>
    );
  }

  if (status === "loading" || row === undefined) {
    if (loadingPhase === "none") {
      return null;
    }
    return (
      <div>
        <RecordSkeleton fieldCount={fields.length} />
        {loadingPhase === "unusual" ? (
          <p className="mt-2 text-meta text-ink-secondary" role="status">
            Still loading. This is unusual at this size.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      {backLink ? (
        <p className="mb-4">
          <Link to={backLink.href} className="text-body text-ink-secondary hover:underline">
            &larr; Back to {backLink.label}
          </Link>
        </p>
      ) : null}
      <header className={`rounded-lg p-6 ${HEADER_TINT_CLASSES[tint]}`}>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            {Icon ? (
              <div
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${HEADER_ICON_TINT_CLASSES[tint]}`}
              >
                <Icon width={24} height={24} />
              </div>
            ) : null}
            <div>
              {deletion ? (
                <p className="mb-1 text-meta text-ink-secondary">
                  Deleted on {formatDateProse(deletion.deletedAt)} by {deletion.deletedByDisplayName}.
                </p>
              ) : null}
              <div className="flex flex-wrap items-center gap-3">
                {/* Owner-requested (2026-08-28), "Focused Sidebar + Global
                 * Command Center": `GlobalTopBar.tsx`'s own second line
                 * already shows this exact title (`useDocumentTitle`, the
                 * same string this page's own `document.title` effect
                 * writes) on desktop — `md:hidden` here removes the
                 * duplicate rather than showing it twice, while phone (no
                 * such header exists there) keeps this as its only title. */}
                <h1 className="text-title font-semibold tracking-title text-ink md:hidden">{identifier(row)}</h1>
                {statusBadge ? statusBadge(row) : null}
              </div>
            </div>
          </div>
          {primaryAction || (secondaryActions && secondaryActions.length > 0) ? (
            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
              {secondaryActions?.map((action) => (
                <Button
                  key={action.label}
                  variant="ghost"
                  className="w-full md:w-auto"
                  onClick={action.onClick}
                  {...(action.disabled !== undefined ? { disabled: action.disabled } : {})}
                  {...(action.ariaLabel !== undefined ? { "aria-label": action.ariaLabel } : {})}
                >
                  {action.label}
                </Button>
              ))}
              {primaryAction ? (
                <Button
                  className="w-full md:w-auto"
                  onClick={primaryAction.onClick}
                  {...(primaryAction.disabled !== undefined ? { disabled: primaryAction.disabled } : {})}
                >
                  {primaryAction.label}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      <div className="mt-6">{keyFacts ? keyFacts(row) : null}</div>

      <div className="divide-y divide-border-hairline">{primaryFields.map((field) => renderField(field))}</div>

      {moreOpen && moreFields.length > 0 ? (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {moreFields.map((field) => renderField(field, "card"))}
        </div>
      ) : null}

      {moreFields.length > 0 ? (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setMoreOpen((open) => !open)}
            aria-expanded={moreOpen}
            className="inline-flex h-tap-target-min items-center text-body font-medium text-accent underline"
          >
            {moreOpen ? "Hide details" : "More details"}
          </button>
        </div>
      ) : null}

      {relatedRecords?.map((section) => <RelatedRecordsSection key={section.title} {...section} />)}

      <div aria-live="polite" className="sr-only">
        {inlineEdits.liveMessage}
      </div>
      {!inlineEdits.isOnline && Object.keys(inlineEdits.cellStates).length > 0 ? (
        <div
          role="status"
          className="fixed inset-x-0 bottom-0 z-10 border-t border-border-hairline bg-warning-quiet px-4 py-3 text-center text-body text-ink"
        >
          You&apos;re offline. Nothing is saving right now.
        </div>
      ) : null}
    </div>
  );
}

/** Loading shape (DESIGN.md § State Patterns: "rendered in the shape of the
 * content that is coming"), a handful of field-row-height bars — generic
 * over the field count, never a specific record type. */
function RecordSkeleton({ fieldCount }: { fieldCount: number }) {
  const rowCount = Math.max(1, Math.min(fieldCount, 6));
  return (
    <div role="presentation" aria-hidden="true">
      <div className="mb-6 h-8 w-1/3 rounded-sm bg-surface-sunken" />
      {Array.from({ length: rowCount }, (_, index) => (
        <div key={index} className="flex min-h-tap-target-min items-center gap-4 border-b border-border-hairline py-2">
          <div className="h-3 w-field-label-col shrink-0 rounded-sm bg-surface-sunken" />
          <div className="h-3 flex-1 rounded-sm bg-surface-sunken" />
        </div>
      ))}
    </div>
  );
}
